import { get, ref, update } from "firebase/database";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole } from "./auth";
import { getKeyDatabase } from "../services/public";
import { LaunchError } from "../utils/errors";
import { canWriteCreativeTemplates } from "../features/creativeTemplates/utils/permissions";
import {
  ANIMATE_IMAGE_OUTRO_NAME_MAX_LENGTH,
  ANIMATE_IMAGE_PROMPT_MAX_LENGTH,
  ANIMATE_IMAGE_PROMPT_NAME_MAX_LENGTH,
} from "../utils/animateImageConstants";

const getLibraryPath = ({ advertiserID }) =>
  `animateImageLibrary/${advertiserID}`;

const assertCanWrite = ({ role }) => {
  if (!canWriteCreativeTemplates({ role })) {
    return { ok: false, reason: "unauthorized" };
  }
  return { ok: true };
};

const listByCreationTime = (value, mapItem) =>
  Object.entries(value || {})
    .map(([id, item]) => mapItem(id, item))
    .filter(Boolean)
    .sort((left, right) => right.creationTime - left.creationTime);

const toPrompt = (id, item) => {
  const name = String(item?.name || "").trim();
  const prompt = String(item?.prompt || "").trim();
  if (!id || !name || !prompt) return null;
  return {
    id,
    name,
    prompt,
    creationTime: Number(item?.creationTime) || 0,
  };
};

const toOutro = (id, item) => {
  const name = String(item?.name || "").trim();
  const videoUrl = String(item?.videoUrl || "").trim();
  if (!id || !name || !videoUrl) return null;
  return {
    id,
    name,
    videoUrl,
    creationTime: Number(item?.creationTime) || 0,
  };
};

const readAdvertiser = (dispatch) => {
  const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
  return { advertiserID, role };
};

export const startListAnimateImagePrompts = () => async (dispatch) => {
  try {
    const { advertiserID } = readAdvertiser(dispatch);
    if (!advertiserID) {
      return { ok: false, reason: "missingAdvertiser", prompts: [] };
    }

    const snapshot = await get(
      ref(db, `${getLibraryPath({ advertiserID })}/prompts`)
    );
    return {
      ok: true,
      prompts: listByCreationTime(snapshot.val(), toPrompt),
    };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startListAnimateImagePrompts",
        tags: { action: "animateImageLibrary" },
        level: "error",
      })
    );
    return { ok: false, reason: "listPromptsFailed", prompts: [] };
  }
};

export const startSaveAnimateImagePrompt =
  ({ name, prompt } = {}) =>
  async (dispatch) => {
    try {
      const { advertiserID, role } = readAdvertiser(dispatch);
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID) return { ok: false, reason: "missingAdvertiser" };

      const nextName = String(name || "").trim();
      const nextPrompt = String(prompt || "").trim();
      if (
        !nextName ||
        nextName.length > ANIMATE_IMAGE_PROMPT_NAME_MAX_LENGTH ||
        !nextPrompt ||
        nextPrompt.length > ANIMATE_IMAGE_PROMPT_MAX_LENGTH
      ) {
        return { ok: false, reason: "invalidPrompt" };
      }

      const promptID = dispatch(getKeyDatabase());
      const creationTime = Date.now();
      await update(ref(db), {
        [`${getLibraryPath({ advertiserID })}/prompts/${promptID}`]: {
          name: nextName,
          prompt: nextPrompt,
          creationTime,
          creationTimeNegative: creationTime * -1,
        },
      });
      return {
        ok: true,
        prompt: {
          id: promptID,
          name: nextName,
          prompt: nextPrompt,
          creationTime,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveAnimateImagePrompt",
          tags: { action: "animateImageLibrary" },
          level: "error",
        })
      );
      return { ok: false, reason: "savePromptFailed" };
    }
  };

export const startDeleteAnimateImagePrompt =
  ({ promptID } = {}) =>
  async (dispatch) => {
    try {
      const { advertiserID, role } = readAdvertiser(dispatch);
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      const nextID = String(promptID || "").trim();
      if (!advertiserID || !nextID) {
        return { ok: false, reason: "missingParameters" };
      }

      await update(ref(db), {
        [`${getLibraryPath({ advertiserID })}/prompts/${nextID}`]: null,
      });
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startDeleteAnimateImagePrompt",
          context: { promptID },
          tags: { action: "animateImageLibrary" },
          level: "error",
        })
      );
      return { ok: false, reason: "deletePromptFailed" };
    }
  };

export const startListAnimateImageOutros = () => async (dispatch) => {
  try {
    const { advertiserID } = readAdvertiser(dispatch);
    if (!advertiserID) {
      return { ok: false, reason: "missingAdvertiser", outros: [] };
    }

    const snapshot = await get(
      ref(db, `${getLibraryPath({ advertiserID })}/outros`)
    );
    return {
      ok: true,
      outros: listByCreationTime(snapshot.val(), toOutro),
    };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startListAnimateImageOutros",
        tags: { action: "animateImageLibrary" },
        level: "error",
      })
    );
    return { ok: false, reason: "listOutrosFailed", outros: [] };
  }
};

export const startSaveAnimateImageOutro =
  ({ name, videoUrl } = {}) =>
  async (dispatch) => {
    try {
      const { advertiserID, role } = readAdvertiser(dispatch);
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID) return { ok: false, reason: "missingAdvertiser" };

      const nextName = String(name || "").trim();
      const nextUrl = String(videoUrl || "").trim();
      if (
        !nextName ||
        nextName.length > ANIMATE_IMAGE_OUTRO_NAME_MAX_LENGTH ||
        !nextUrl
      ) {
        return { ok: false, reason: "invalidOutro" };
      }

      const outroID = dispatch(getKeyDatabase());
      const creationTime = Date.now();
      const outro = {
        id: outroID,
        name: nextName,
        videoUrl: nextUrl,
        creationTime,
      };
      await update(ref(db), {
        [`${getLibraryPath({ advertiserID })}/outros/${outroID}`]: {
          name: nextName,
          videoUrl: nextUrl,
          creationTime,
          creationTimeNegative: creationTime * -1,
        },
        [`${getLibraryPath({ advertiserID })}/selectedOutroID`]: outroID,
      });
      return { ok: true, outro };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveAnimateImageOutro",
          tags: { action: "animateImageLibrary" },
          level: "error",
        })
      );
      return { ok: false, reason: "saveOutroFailed" };
    }
  };

export const startDeleteAnimateImageOutro =
  ({ outroID } = {}) =>
  async (dispatch) => {
    try {
      const { advertiserID, role } = readAdvertiser(dispatch);
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      const nextID = String(outroID || "").trim();
      if (!advertiserID || !nextID) {
        return { ok: false, reason: "missingParameters" };
      }

      const selectedSnap = await get(
        ref(db, `${getLibraryPath({ advertiserID })}/selectedOutroID`)
      );
      const clearedSelection = String(selectedSnap.val() || "") === nextID;
      const updates = {
        [`${getLibraryPath({ advertiserID })}/outros/${nextID}`]: null,
      };
      if (clearedSelection) {
        updates[`${getLibraryPath({ advertiserID })}/selectedOutroID`] = null;
      }

      await update(ref(db), updates);
      return { ok: true, clearedSelection };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startDeleteAnimateImageOutro",
          context: { outroID },
          tags: { action: "animateImageLibrary" },
          level: "error",
        })
      );
      return { ok: false, reason: "deleteOutroFailed" };
    }
  };

export const startSetSelectedAnimateImageOutro =
  ({ outroID } = {}) =>
  async (dispatch) => {
    try {
      const { advertiserID, role } = readAdvertiser(dispatch);
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID) return { ok: false, reason: "missingAdvertiser" };

      const nextID = String(outroID || "").trim();
      await update(ref(db), {
        [`${getLibraryPath({ advertiserID })}/selectedOutroID`]: nextID || null,
      });
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSetSelectedAnimateImageOutro",
          context: { outroID },
          tags: { action: "animateImageLibrary" },
          level: "error",
        })
      );
      return { ok: false, reason: "setSelectedOutroFailed" };
    }
  };
